import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ResponsiveLayout, ResponsivePageHeader } from '../components/ResponsiveLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Activity, AlertTriangle, DollarSign, Shield, Users,
  Play, Clock, CheckCircle, ChevronLeft, Crown,
  RefreshCw, FileText, ArrowLeft, Zap, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_CONFIG = {
  high: { label: 'High', className: 'bg-red-500/20 text-red-300 border-red-500/30' },
  medium: { label: 'Medium', className: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  low: { label: 'Low', className: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
};

const CATEGORY_CONFIG = {
  urgent: { label: 'Urgent', icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
  finance: { label: 'Finance', icon: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  support: { label: 'Support', icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  admin: { label: 'Admin Queue', icon: Users, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
};

const STATUS_CONFIG = {
  new: { label: 'New', className: 'bg-white/10 text-white/80', next: 'acknowledged' },
  acknowledged: { label: 'Acknowledged', className: 'bg-blue-500/20 text-blue-300', next: 'resolved' },
  resolved: { label: 'Resolved', className: 'bg-emerald-500/20 text-emerald-300', next: null },
};

const OpsBriefing = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeView, setActiveView] = useState('current');
  const [taskFilter, setTaskFilter] = useState('all');

  const fetchLatest = useCallback(async () => {
    try {
      const res = await api.get('/ops/briefings/latest');
      setBriefing(res.data.briefing);
      setTasks(res.data.tasks || []);
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error('Failed to load briefing');
      }
    }
  }, [api]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/ops/briefings');
      setHistory(res.data.briefings || []);
    } catch {
      // silent
    }
  }, [api]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchLatest(), fetchHistory()]);
      setLoading(false);
    };
    load();
  }, [fetchLatest, fetchHistory]);

  const runBriefing = async () => {
    setRunning(true);
    try {
      const res = await api.post('/ops/run-briefing');
      setBriefing({
        briefing_id: res.data.briefing_id,
        created_at: res.data.created_at,
        summary_text: res.data.summary_text,
        category_counts: res.data.category_counts,
        generated_by: res.data.generated_by,
        task_count: res.data.task_count,
      });
      setTasks(res.data.tasks || []);
      toast.success(`Briefing generated — ${res.data.task_count} tasks flagged`);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run briefing');
    } finally {
      setRunning(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.patch(`/ops/tasks/${taskId}`, { status: newStatus });
      setTasks(prev => prev.map(t =>
        t.task_id === taskId ? { ...t, status: newStatus } : t
      ));
      toast.success(`Task marked as ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const categoryCounts = briefing?.category_counts || { urgent: 0, finance: 0, support: 0, admin: 0 };
  const filteredTasks = taskFilter === 'all' ? tasks : tasks.filter(t => t.category === taskFilter);

  const sidebarItems = [
    { id: 'back', icon: ArrowLeft, label: 'Back to Dashboard' },
  ];

  const handleNavClick = (id) => {
    if (id === 'back') navigate('/platform-owner');
  };

  if (loading) {
    return (
      <ResponsiveLayout
        sidebarItems={sidebarItems}
        activeTab="ops"
        onTabChange={handleNavClick}
        headerTitle="Ops Briefing"
        headerIcon={Activity}
        user={user}
      >
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout
      sidebarItems={sidebarItems}
      activeTab="ops"
      onTabChange={handleNavClick}
      headerTitle="Ops Briefing"
      headerIcon={Activity}
      user={user}
    >
      <div className="space-y-6" data-testid="ops-briefing-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <ResponsivePageHeader
            title="Operations Briefing"
            subtitle="Daily operational health summary"
            badge={
              <Badge className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white border-0 px-3 py-1 text-xs">
                <Activity className="w-3 h-3 mr-1.5" />
                Phase 1
              </Badge>
            }
          />
          <Button
            onClick={runBriefing}
            disabled={running}
            data-testid="run-briefing-btn"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-medium px-6 shrink-0"
          >
            {running ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Run Briefing</>
            )}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="ops-summary-cards">
          {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const count = categoryCounts[key] || 0;
            return (
              <Card
                key={key}
                className={`border cursor-pointer transition-all hover:scale-[1.02] ${cfg.bg} ${taskFilter === key ? 'ring-2 ring-white/30' : ''}`}
                onClick={() => setTaskFilter(taskFilter === key ? 'all' : key)}
                data-testid={`ops-card-${key}`}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-2">
                    <Icon className={`w-5 h-5 ${cfg.color}`} />
                    <span className={`text-2xl sm:text-3xl font-bold ${cfg.color}`}>{count}</span>
                  </div>
                  <p className="text-xs sm:text-sm text-white/60">{cfg.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* View Toggle */}
        <div className="flex gap-2">
          {['current', 'history'].map(v => (
            <Button
              key={v}
              variant={activeView === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView(v)}
              className={activeView === v ? 'bg-white/10 text-white border-white/20' : 'text-white/50 border-white/10'}
            >
              {v === 'current' ? <><FileText className="w-3.5 h-3.5 mr-1.5" /> Today's Brief</> : <><Clock className="w-3.5 h-3.5 mr-1.5" /> History</>}
            </Button>
          ))}
        </div>

        {/* Current Briefing View */}
        {activeView === 'current' && (
          <div className="space-y-6">
            {/* AI Summary */}
            {briefing ? (
              <Card className="border border-white/10 bg-white/[0.03]" data-testid="ops-summary-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base sm:text-lg text-white flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-400" />
                      Briefing Summary
                    </CardTitle>
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <span>{new Date(briefing.created_at).toLocaleString()}</span>
                      <Badge variant="outline" className="text-[10px] border-white/20 text-white/40">
                        {briefing.generated_by === 'ai' ? 'AI' : 'Auto'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="whitespace-pre-wrap text-sm text-white/80 leading-relaxed font-mono bg-black/20 rounded-lg p-4 border border-white/5">
                    {briefing.summary_text}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border border-white/10 bg-white/[0.03]">
                <CardContent className="py-12 text-center">
                  <Activity className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No briefing generated yet.</p>
                  <p className="text-white/30 text-xs mt-1">Click "Run Briefing" to generate your first ops summary.</p>
                </CardContent>
              </Card>
            )}

            {/* Tasks */}
            <div data-testid="ops-task-list">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white/70">
                  Tasks {taskFilter !== 'all' && <span className="text-white/40">({CATEGORY_CONFIG[taskFilter]?.label})</span>}
                  <span className="ml-2 text-white/30">({filteredTasks.length})</span>
                </h3>
                {taskFilter !== 'all' && (
                  <Button variant="ghost" size="sm" onClick={() => setTaskFilter('all')} className="text-xs text-white/40 h-7">
                    Clear filter
                  </Button>
                )}
              </div>

              {filteredTasks.length === 0 ? (
                <Card className="border border-white/10 bg-white/[0.03]">
                  <CardContent className="py-8 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-500/40 mx-auto mb-2" />
                    <p className="text-white/40 text-sm">
                      {tasks.length === 0 ? 'No tasks flagged.' : 'No tasks in this category.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {filteredTasks.map(task => {
                    const catCfg = CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.admin;
                    const priCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                    const statusCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.new;
                    const CatIcon = catCfg.icon;

                    return (
                      <Card
                        key={task.task_id}
                        className={`border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors ${task.action_route ? 'cursor-pointer' : ''}`}
                        data-testid={`ops-task-${task.task_id}`}
                        onClick={() => task.action_route && navigate(task.action_route)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <CatIcon className={`w-4 h-4 mt-0.5 shrink-0 ${catCfg.color}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                <span className="text-sm font-medium text-white/90">{task.title}</span>
                                <Badge className={`text-[10px] px-1.5 py-0 border ${priCfg.className}`}>{priCfg.label}</Badge>
                                <Badge className={`text-[10px] px-1.5 py-0 ${statusCfg.className}`}>{statusCfg.label}</Badge>
                                {task.priority_score != null && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-white/15 text-white/50 font-mono" data-testid={`task-score-${task.task_id}`}>
                                    Score: {task.priority_score}
                                  </Badge>
                                )}
                                {task.metric_value != null && task.threshold != null && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-white/15 text-white/40 font-mono">
                                    {task.metric_value}{task.metric_unit === 'hours' ? 'h' : 'd'} / {task.threshold}{task.metric_unit === 'hours' ? 'h' : 'd'} threshold
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-white/60 mb-1.5" data-testid={`task-reason-${task.task_id}`}>{task.reason_flagged || task.reason}</p>
                              {task.impact_summary && (
                                <p className="text-xs text-amber-400/70 mb-2" data-testid={`task-impact-${task.task_id}`}>{task.impact_summary}</p>
                              )}
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-xs text-white/30 flex-1">{task.suggested_action}</p>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  {task.action_route && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); navigate(task.action_route); }}
                                      className="text-xs h-6 px-2.5 border-white/15 text-white/70 hover:text-white hover:bg-white/10"
                                      data-testid={`task-action-${task.task_id}`}
                                    >
                                      {task.action_label || 'Open'} <ExternalLink className="w-3 h-3 ml-1" />
                                    </Button>
                                  )}
                                  {statusCfg.next && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); updateTaskStatus(task.task_id, statusCfg.next); }}
                                      className="text-xs h-6 px-2 text-white/50 hover:text-white/80"
                                      data-testid={`task-advance-${task.task_id}`}
                                    >
                                      {statusCfg.next === 'acknowledged' ? 'Acknowledge' : 'Resolve'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* History View */}
        {activeView === 'history' && (
          <div data-testid="ops-history-list">
            {history.length === 0 ? (
              <Card className="border border-white/10 bg-white/[0.03]">
                <CardContent className="py-8 text-center">
                  <Clock className="w-8 h-8 text-white/20 mx-auto mb-2" />
                  <p className="text-white/40 text-sm">No briefing history yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {history.map(h => (
                  <Card key={h.briefing_id} className="border border-white/10 bg-white/[0.03]" data-testid={`ops-history-${h.briefing_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-white/80 font-medium">{h.briefing_id}</p>
                          <p className="text-xs text-white/40 mt-0.5">
                            {new Date(h.created_at).toLocaleString()} — {h.task_count} tasks — {h.generated_by === 'ai' ? 'AI Generated' : 'Auto Generated'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {Object.entries(h.category_counts || {}).map(([cat, count]) => {
                            if (count === 0) return null;
                            const cfg = CATEGORY_CONFIG[cat];
                            if (!cfg) return null;
                            return (
                              <Badge key={cat} variant="outline" className={`text-[10px] ${cfg.color} border-white/10`}>
                                {cfg.label}: {count}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </ResponsiveLayout>
  );
};

export default OpsBriefing;
